/**
 * Mock Debt Data
 *
 * Four simple debts for development/preview purposes.
 * All debts: creditor_name = "Intrum Portugal", original_creditor = "Spark Energy"
 */

import type { BalanceBreakdown, DebtData, DebtPaymentPlan, TransactionItem } from '../types/debt';

// =============================================================================
// Mock Transfers
// =============================================================================

// Debt 2: Two partial payments (half paid)
const debt2Transactions: TransactionItem[] = [
  {
    transaction_id: 'tx_001',
    type: 'payment',
    created_at: '2024-06-01',
    direction: 'credit',
    amount_millicents: 30000000, // €300
    description: 'Partial payment',
    invoice_group_key: null,
    metadata: {},
    transfers: ['txn_001'],
  },
  {
    transaction_id: 'tx_002',
    type: 'payment',
    created_at: '2024-07-01',
    direction: 'credit',
    amount_millicents: 20000000, // €200
    description: 'Partial payment',
    invoice_group_key: null,
    metadata: {},
    transfers: ['txn_002'],
  },
];

// Debt 3: Payment plan installments
const debt3Transactions: TransactionItem[] = [
  {
    transaction_id: 'tx_003',
    type: 'payment',
    created_at: '2024-06-15',
    direction: 'credit',
    amount_millicents: 20000000, // €200
    description: 'Payment plan installment 1/6',
    invoice_group_key: null,
    metadata: { installment: 1 },
    transfers: ['txn_003'],
  },
  {
    transaction_id: 'tx_004',
    type: 'payment',
    created_at: '2024-07-15',
    direction: 'credit',
    amount_millicents: 20000000, // €200
    description: 'Payment plan installment 2/6',
    invoice_group_key: null,
    metadata: { installment: 2 },
    transfers: ['txn_004'],
  },
];

// Debt 4: Full payment
const debt4Transactions: TransactionItem[] = [
  {
    transaction_id: 'tx_005',
    type: 'payment',
    created_at: '2024-05-20',
    direction: 'credit',
    amount_millicents: 70000000, // €700
    description: 'Full settlement',
    invoice_group_key: null,
    metadata: {},
    transfers: ['txn_005'],
  },
];

// =============================================================================
// Mock Payment Plan (Debt 3)
// =============================================================================

const debt3PaymentPlan: DebtPaymentPlan = {
  externalPaymentPlanId: 'pln_mock_001',
  status: 'active',
  totalPayments: 6,
  completedPayments: 2,
  fullPlan: {
    id: 'pln_mock_001',
    currency: 'EUR',
    description: 'Payment plan for Spark Energy debt',
    invoice_group_key: null,
    metadata: {},
    source: 'src_mock',
    status: 'active',
    target: 'tgt_mock',
    tenant: 'tnt_mock',
    schedule: [
      {
        date: '2024-06-15',
        subunits: 20000, // €200.00
        timeZone: 'Europe/Lisbon',
        reference: 'Payment 1/6',
        payment: { id: 'pmt_001', status: 'succeeded' },
      },
      {
        date: '2024-07-15',
        subunits: 20000,
        timeZone: 'Europe/Lisbon',
        reference: 'Payment 2/6',
        payment: { id: 'pmt_002', status: 'succeeded' },
      },
      {
        date: '2024-08-15',
        subunits: 20000,
        timeZone: 'Europe/Lisbon',
        reference: 'Payment 3/6',
      },
      {
        date: '2024-09-15',
        subunits: 20000,
        timeZone: 'Europe/Lisbon',
        reference: 'Payment 4/6',
      },
      {
        date: '2024-10-15',
        subunits: 20000,
        timeZone: 'Europe/Lisbon',
        reference: 'Payment 5/6',
      },
      {
        date: '2024-11-15',
        subunits: 20000,
        timeZone: 'Europe/Lisbon',
        reference: 'Payment 6/6',
      },
    ],
  },
};

// =============================================================================
// Helper to create balance breakdown
// =============================================================================

function breakdown(total: number): BalanceBreakdown {
  return {
    principal: total * 0.8,
    interest: total * 0.12,
    fees: total * 0.08,
    total,
  };
}

// =============================================================================
// The Four Mock Debts
// =============================================================================

/**
 * Debt 1: Engaged customer, no payments yet
 * journey_status: active, journey_node: engaged
 */
const debt1: DebtData = {
  id: 'case_mock_001',
  externalCaseRef: 'EXT-SPARK-001',
  originalCreditor: 'Spark Energy',
  creditorName: 'Intrum Portugal',
  description: 'Energy bill',
  currency: 'EUR',
  accountNumber: 'ACCT-001',
  caseStatus: 'active',
  journeyStatus: 'active',
  journeyNode: 'engaged',
  status: 'unpaid',
  originalAmount: 1100,
  currentBalance: 1100,
  originalBreakdown: breakdown(1100),
  outstandingBreakdown: breakdown(1100),
  adjustmentBreakdown: breakdown(0),
  paymentBreakdown: breakdown(0),
  creditApplied: 0,
  transactions: [],
  paymentPlan: null,
  isAccountPlanMember: false,
};

/**
 * Debt 2: Paying customer, half paid
 * journey_status: active, journey_node: paying
 */
const debt2: DebtData = {
  id: 'case_mock_002',
  externalCaseRef: 'EXT-SPARK-002',
  originalCreditor: 'Spark Energy',
  creditorName: 'Intrum Portugal',
  description: 'Energy bill',
  currency: 'EUR',
  accountNumber: 'ACCT-002',
  caseStatus: 'active',
  journeyStatus: 'active',
  journeyNode: 'paying',
  status: 'in-progress',
  originalAmount: 1000,
  currentBalance: 500,
  originalBreakdown: breakdown(1000),
  outstandingBreakdown: breakdown(500),
  adjustmentBreakdown: breakdown(0),
  paymentBreakdown: breakdown(500),
  creditApplied: 0,
  transactions: debt2Transactions,
  paymentPlan: null,
  isAccountPlanMember: false,
};

/**
 * Debt 3: Active payment plan
 * journey_status: active, journey_node: paying
 */
const debt3: DebtData = {
  id: 'case_mock_003',
  externalCaseRef: 'EXT-SPARK-003',
  originalCreditor: 'Spark Energy',
  creditorName: 'Intrum Portugal',
  description: 'Energy bill',
  currency: 'EUR',
  accountNumber: 'ACCT-003',
  caseStatus: 'active',
  journeyStatus: 'active',
  journeyNode: 'paying',
  status: 'in-progress',
  originalAmount: 1200,
  currentBalance: 800,
  originalBreakdown: breakdown(1200),
  outstandingBreakdown: breakdown(800),
  adjustmentBreakdown: breakdown(0),
  paymentBreakdown: breakdown(400),
  creditApplied: 0,
  transactions: debt3Transactions,
  paymentPlan: debt3PaymentPlan,
  isAccountPlanMember: false,
};

/**
 * Debt 4: Resolved, fully paid
 * journey_status: active, journey_node: resolved
 */
const debt4: DebtData = {
  id: 'case_mock_004',
  externalCaseRef: 'EXT-SPARK-004',
  originalCreditor: 'Spark Energy',
  creditorName: 'Intrum Portugal',
  description: 'Energy bill',
  currency: 'EUR',
  accountNumber: 'ACCT-004',
  caseStatus: 'closed',
  journeyStatus: 'active',
  journeyNode: 'resolved',
  status: 'completed',
  originalAmount: 700,
  currentBalance: 0,
  originalBreakdown: breakdown(700),
  outstandingBreakdown: breakdown(0),
  adjustmentBreakdown: breakdown(0),
  paymentBreakdown: breakdown(700),
  creditApplied: 0,
  transactions: debt4Transactions,
  paymentPlan: null,
  isAccountPlanMember: false,
};

// =============================================================================
// Exports
// =============================================================================

/** All mock debts */
export const MOCK_DEBTS: DebtData[] = [debt1, debt2, debt3, debt4];

/** Get mock debts (same data for all contexts) */
export function getMockDebts(): DebtData[] {
  return MOCK_DEBTS;
}

/** Get mock debts for context (returns same data regardless of context) */
export function getMockDebtsForContext(_operatorName: string, _portfolioName?: string | null, _clientName?: string | null): DebtData[] {
  return MOCK_DEBTS;
}

// Stable IDs for reference
export const MOCK_DEBT_ID = 'case_mock_001';
export const MOCK_DEBT_ID_PAYING = 'case_mock_002';
export const MOCK_DEBT_ID_PLAN_ACTIVE = 'case_mock_003';
export const MOCK_DEBT_ID_RESOLVED = 'case_mock_004';
